import type { Polygon } from "geojson";
import { wkbToPolygon } from "../../../../../utils/wkbUtils";
import { fx_clienteMapaRegistro } from "./MapaRegistroFinca";

/** La geometría de UN lote del paso 4, leída de la base.
 *
 *  **Para qué.** Hoy, al poner un lote en modo Editar
 *  (`LoteSplitMapRender._editarVerticesDeAsync`): la inserción de un vértice
 *  nombra el tramo por su número en el anillo, así que el motor tiene que tener
 *  el anillo tal como está en la base. Cuando el que se ve, el que se edita y el
 *  que guarda el servidor salen de lugares distintos, alcanza una diferencia
 *  mínima para que la operación caiga en otro lado.
 *
 *  **Dividir ya no la usa** (2026-10-27, pedido del usuario: un viaje menos,
 *  0,62 s medido en pruebas, cada vez que se tocaba un lote). El snap del corte
 *  sólo necesita las posiciones de los vértices, y la copia del estado ya es la
 *  de la base (ver `LoteSplitToolbar.comenzarCorteDeAsync`, que dice cómo
 *  revertirlo).
 *
 *  **Va por gRPC** (`LeerLoteTemporal`) desde el 2026-10-11. Antes era un
 *  GetFeature de WFS directo a GeoServer, sin filtrar por registro: con el id
 *  de un lote ajeno se leía su geometría. El servidor ahora comprueba que el
 *  registro sea del cliente y que el lote sea de ese registro. El nombre de la
 *  clase quedó de cuando iba por WFS.
 *
 *  **No se renderiza.** El polígono queda en memoria; lo único que Leaflet
 *  dibuja es la línea de corte y su punto de snap. Mismo criterio que los
 *  vértices del modo Editar del paso 3.
 */
export class LotesWfsService {
  /** La geometría del lote, o `null` si el servidor no contestó o el lote no
   *  existe.
   *
   *  `null` y no un polígono vacío: para el llamador son cosas distintas. Sin
   *  geometría hay que caer en la copia del modelo, que es el camino degradado;
   *  un polígono vacío se leería como "este lote no tiene forma". */
  async leerAsync(
    registroId: string | null,
    loteId: string,
  ): Promise<Polygon | null> {
    if (!registroId || !loteId) return null;

    try {
      const respuesta = await fx_clienteMapaRegistro().leerLoteTemporal({
        registroId,
        loteId,
      });
      return respuesta.geometria ? wkbToPolygon(respuesta.geometria) : null;
    } catch (error) {
      // Sin respuesta el modo Dividir sigue andando contra la geometría del
      // modelo. Es un camino degradado, no un error que el usuario tenga que
      // ver: lo que se pierde es que el snap salga de la base.
      console.error("No se pudo leer el lote del registro", error);
      return null;
    }
  }
}
